import React, { useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Calendar, Users, Award } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { useAuth } from "src/contexts/AuthContext";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";
import UclaBsnContestPage from "./UclaBsnContestPage";
import BabsonBcaContestPage from "./BabsonBcaContestPage";
import NormalWeeklyContestPage from "./normalWeeklyContestPage";

interface ContestStats {
  totalParticipants: number;
  daysLeft: number;
  prizePool: string;
}

interface CaseCompetition {
  title: string;
  description: string;
  deadline: string;
  status: "upcoming" | "active" | "completed";
}

const WeeklyContestPage = () => {
  const { userDoc } = useAuth();
  const navigate = usePartnerAwareNavigate();
  const [stats] = useState<ContestStats>({
    totalParticipants: 42,
    daysLeft: 5,
    prizePool: "$1,000",
  });
  const [clubCases, setClubCases] = useState<CaseCompetition[]>([]);

  if (userDoc?.studentFrom === "babson-bca") {
    return <BabsonBcaContestPage />;
  }

  if (userDoc?.studentFrom === "ucla-bsn") {
    return <UclaBsnContestPage />;
  }

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
    },
  };

  if (userDoc?.studentFrom === "ucla-bsn") {
    console.log("check userDoc", userDoc);
    console.log("check userDoc?.studentFrom", userDoc?.studentFrom);
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-center mb-12"
        >
          <motion.div
            variants={itemVariants}
            className="inline-block p-3 bg-blue-100 rounded-full mb-6"
          >
            <Trophy className="w-8 h-8 text-blue-600" />
          </motion.div>
          <motion.h1
            variants={itemVariants}
            className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-transparent bg-clip-text"
          >
            UCLA BSN Club Case Competitions
          </motion.h1>
          <motion.p
            variants={itemVariants}
            className="text-gray-600 text-lg max-w-2xl mx-auto"
          >
            Exclusive case competitions and workshops for UCLA BSN members.
          </motion.p>
        </motion.div>

        {clubCases.map((caseComp, index) => (
          <motion.div
            key={index}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 mb-6"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold">{caseComp.title}</h3>
              <span
                className={`px-3 py-1 rounded-full text-sm ${
                  caseComp.status === "active"
                    ? "bg-green-100 text-green-700"
                    : caseComp.status === "upcoming"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {caseComp.status.charAt(0).toUpperCase() +
                  caseComp.status.slice(1)}
              </span>
            </div>
            <p className="text-gray-600 mb-4">{caseComp.description}</p>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">
                <Calendar className="w-4 h-4 inline mr-2" />
                Deadline: {caseComp.deadline}
              </span>
              <Button
                onClick={() =>
                  navigate(
                    `/club-cases/${caseComp.title
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`
                  )
                }
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                View Details
              </Button>
            </div>
          </motion.div>
        ))}

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8"
        >
          <motion.div
            variants={itemVariants}
            className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
          >
            <h3 className="text-xl font-bold mb-4">Club Benefits</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-blue-600">💼</span>
                <span>Exclusive access to club-only case competitions</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600">🤝</span>
                <span>Network with industry professionals</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600">📚</span>
                <span>Weekly consulting workshops and training</span>
              </li>
            </ul>
          </motion.div>
          <motion.div
            variants={itemVariants}
            className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
          >
            <h3 className="text-xl font-bold mb-4">Resources</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-blue-600">📖</span>
                <span>Case interview preparation materials</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600">👥</span>
                <span>Mentorship opportunities</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600">🎯</span>
                <span>Career development workshops</span>
              </li>
            </ul>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // Original content for non-UCLA BSN students
  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <NormalWeeklyContestPage />
    </div>
  );
};

export default WeeklyContestPage;
